import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '../lib/auth'
import { supabase } from '../lib/supabase'

const SYSTEM_PROMPT_MEMBER = `You are ScamView Assistant, an AI helper built into ScamView.ai — a job scam detection and recruiter verification platform. You help paying members get the most out of the product.

ABOUT SCAMVIEW:
- ScamView scans recruiter emails, phone numbers, and job offers for fraud using AI + DNS checks + community intelligence
- Risk scores run from 1-10 (1-3 safe, 4-6 suspicious, 7-10 high risk/scam)
- HireMail: Premium members get a username@hiremail.net alias to put on their resume. Every recruiter email sent to that address is automatically scanned before forwarding to their real inbox with a risk score banner attached
- Basic plan: $9.99/month, 300 credits/month, email scanning, 7 scan history
- Premium plan: $19.99/month, 500 credits/month, HireMail protected inbox, phone scanning, brand impersonation detection, unlimited history
- Credits: each scan costs 1 credit. Extra credits available at 100 for $1

YOUR ROLE:
- Help members use the product effectively
- Educate about job scam tactics and red flags
- Answer questions about HireMail setup and usage
- Help interpret scan results
- Explain what detection signals mean
- Guide members on what to do if they've been scammed
- Keep responses concise, warm, and practical
- Never give legal or financial advice
- If asked something outside your scope, say so clearly and suggest they contact support

TONE: Professional but approachable. You are a knowledgeable security ally, not a corporate bot.`

const SYSTEM_PROMPT_VISITOR = `You are ScamView Assistant, an AI helper on ScamView.ai — a job scam detection platform that protects job seekers from fake recruiters, fraudulent job offers, and employment scams.

YOUR ROLE FOR VISITORS:
- Educate about job scam tactics, red flags, and how to stay safe
- Explain what ScamView does and how it can help them
- Answer questions about job scam trends in 2025/2026
- Guide them toward trying the free scan or signing up
- Be genuinely helpful — not a pushy sales bot
- If they describe a suspicious situation, help them identify red flags

ABOUT SCAMVIEW:
- Free scan available — no account needed, paste any recruiter email or phone number
- Basic plan: $9.99/month with 14-day free trial
- Premium plan: $19.99/month includes HireMail protected inbox that auto-scans recruiter emails before they reach your real inbox
- Over 40% of active job seekers encounter scam recruiters — ScamView catches them automatically

COMMON SCAM RED FLAGS TO EDUCATE ABOUT:
- Non-company email addresses (gmail/yahoo from "recruiters")
- Urgency language ("act now", "limited time offer")
- Upfront payment requests for equipment or training
- Vague job descriptions with unusually high salaries
- Requests for SSN or bank info early in process
- Job offers without interviews
- Brand impersonation (fake Amazon, Google, FedEx recruiters)

TONE: Warm, knowledgeable, protective. You're like a savvy friend who knows how to spot scams.`

const SUGGESTED_QUESTIONS_MEMBER = [
  "How do I use my HireMail address?",
  "What does a risk score of 7 mean?",
  "How do I read my detection signals?",
  "I think I was scammed — what do I do?",
]

const SUGGESTED_QUESTIONS_VISITOR = [
  "How can I tell if a recruiter is fake?",
  "What is HireMail?",
  "How does ScamView work?",
  "I got a suspicious job offer — help!",
]

export default function ScamViewChat() {
  const { user, profile } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasGreeted, setHasGreeted] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const isMember = !!user
  const systemPrompt = isMember ? SYSTEM_PROMPT_MEMBER : SYSTEM_PROMPT_VISITOR
  const suggestedQuestions = isMember ? SUGGESTED_QUESTIONS_MEMBER : SUGGESTED_QUESTIONS_VISITOR

  const greeting = isMember
    ? `Hi ${profile?.first_name || 'there'} 👋 I'm your ScamView Assistant. Ask me anything about your scans, HireMail, or how to spot job scams.`
    : `Hi there 👋 I'm the ScamView Assistant. I can help you spot fake recruiters, understand job scam tactics, or show you how ScamView protects job seekers. What's on your mind?`

  useEffect(() => {
    if (isOpen && !hasGreeted) {
      setMessages([{ role: 'assistant', content: greeting }])
      setHasGreeted(true)
    }
  }, [isOpen, hasGreeted])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    }
  }, [messages, isOpen])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  const sendMessage = async (text) => {
    const userText = text || input.trim()
    if (!userText || loading) return

    setInput('')
    const newMessages = [...messages, { role: 'user', content: userText }]
    setMessages(newMessages)
    setLoading(true)

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 20000)

    try {
      const { data, error } = await supabase.functions.invoke('chat', {
        body: {
          system: systemPrompt,
          messages: newMessages.map(m => ({ role: m.role, content: m.content }))
        },
        signal: controller.signal
      })
      clearTimeout(timeout)

      if (error) throw error
      const assistantText = data?.text || "I'm having trouble responding right now. Please try again."

      setMessages(prev => [...prev, { role: 'assistant', content: assistantText }])
    } catch (err) {
      clearTimeout(timeout)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: err.name === 'AbortError'
          ? "Response took too long. Please try again."
          : "Sorry, I'm having connection issues. Please try again in a moment."
      }])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const showSuggestions = messages.length <= 1

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] flex flex-col"
          style={{
            height: '520px',
            background: 'linear-gradient(145deg, #0d1321 0%, #0a0e17 100%)',
            border: '1px solid rgba(6,182,212,0.2)',
            borderRadius: '20px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 40px rgba(6,182,212,0.08)',
            animation: 'chatSlideUp 0.25s cubic-bezier(0.34,1.56,0.64,1) forwards'
          }}>

          {/* Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            background: 'linear-gradient(135deg, rgba(6,182,212,0.08) 0%, transparent 100%)',
            borderRadius: '20px 20px 0 0',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px',
                  background: 'linear-gradient(135deg, rgba(6,182,212,0.3), rgba(6,182,212,0.1))',
                  borderRadius: '10px',
                  border: '1px solid rgba(6,182,212,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="rgb(6,182,212)">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <div style={{ color: 'white', fontWeight: '700', fontSize: '14px', fontFamily: "'Orbitron', sans-serif", letterSpacing: '0.02em' }}>
                    Ask ScamView
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e' }} />
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Online</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)',
                  border: 'none', borderRadius: '8px', width: '28px', height: '28px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px', transition: 'all 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.color = 'white'}
                onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
              >
                ×
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px',
            display: 'flex', flexDirection: 'column', gap: '12px',
            scrollbarWidth: 'thin', scrollbarColor: 'rgba(6,182,212,0.2) transparent'
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                animation: 'msgFadeIn 0.2s ease-out forwards'
              }}>
                <div style={{
                  maxWidth: '82%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user'
                    ? 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))'
                    : 'rgba(255,255,255,0.05)',
                  border: msg.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Suggested questions */}
            {showSuggestions && messages.length === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    style={{
                      background: 'rgba(6,182,212,0.06)',
                      border: '1px solid rgba(6,182,212,0.15)',
                      borderRadius: '10px',
                      padding: '8px 12px',
                      color: 'rgba(6,182,212,0.9)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      fontFamily: 'inherit'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'rgba(6,182,212,0.12)'
                      e.currentTarget.style.borderColor = 'rgba(6,182,212,0.3)'
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'rgba(6,182,212,0.06)'
                      e.currentTarget.style.borderColor = 'rgba(6,182,212,0.15)'
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '16px 16px 16px 4px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', gap: '4px', alignItems: 'center'
                }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      background: 'rgba(6,182,212,0.7)',
                      animation: `typingDot 1.2s ease-in-out ${i * 0.2}s infinite`
                    }} />
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px 16px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            flexShrink: 0
          }}>
            <div style={{
              display: 'flex', gap: '8px', alignItems: 'flex-end',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '14px',
              padding: '8px 8px 8px 14px',
              transition: 'border-color 0.2s'
            }}
              onFocusCapture={e => e.currentTarget.style.borderColor = 'rgba(6,182,212,0.4)'}
              onBlurCapture={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about scams or how to use ScamView..."
                disabled={loading}
                rows={1}
                style={{
                  flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  color: 'rgba(255,255,255,0.9)', fontSize: '13px', lineHeight: '1.5',
                  resize: 'none', fontFamily: 'inherit',
                  maxHeight: '80px', overflowY: 'auto',
                  scrollbarWidth: 'none'
                }}
                onInput={e => {
                  e.target.style.height = 'auto'
                  e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px'
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                style={{
                  width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
                  background: input.trim() && !loading
                    ? 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))'
                    : 'rgba(255,255,255,0.06)',
                  border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                  transform: input.trim() && !loading ? 'scale(1)' : 'scale(0.95)'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.18)', fontSize: '10px', textAlign: 'center', marginTop: '8px' }}>
              Powered by ScamView AI · Not legal advice
            </p>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          position: 'fixed', bottom: '20px', right: '20px', zIndex: 50,
          width: '56px', height: '56px', borderRadius: '50%',
          background: isOpen
            ? 'rgba(255,255,255,0.1)'
            : 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))',
          border: isOpen ? '1px solid rgba(255,255,255,0.2)' : 'none',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: isOpen ? 'none' : '0 8px 32px rgba(6,182,212,0.4), 0 0 0 0 rgba(6,182,212,0.4)',
          animation: isOpen ? 'none' : 'bubblePulse 3s ease-in-out infinite',
          transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)'
        }}
        aria-label="Open ScamView chat"
      >
        {isOpen ? (
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.7)" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* CSS Animations */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes msgFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes typingDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes bubblePulse {
          0%, 100% { box-shadow: 0 8px 32px rgba(6,182,212,0.4), 0 0 0 0 rgba(6,182,212,0.3); }
          50% { box-shadow: 0 8px 32px rgba(6,182,212,0.4), 0 0 0 8px rgba(6,182,212,0); }
        }
      `}</style>
    </>
  )
}
